import { PatientDuplicatePairDto, SortDirection } from '@lmt/shared-types';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  getFormattedDateRange,
  getFormattedDateTime,
} from '@lmt/unicorn-shared/app/components/patients/common';
import ListIcon from '@lmt/unicorn-shared/app/icons/list.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useEventCallback,
} from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { ScrollToTop } from '../../common/table/helper';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import { getNumberPostfix } from '../form/components/common';
import { columnKeys, columnStyles } from './common';
import { DuplicateCell } from './DuplicateCell';

import styles from './PatientDuplicateTable.module.scss';

type Props = {
  isNoDuplicatesFound: boolean;
  data: PatientDuplicatePairDto[];
  selectedPairs: number[];
  setSelectedPairs: (pairs: number[]) => void;
  setSorting: (sorting: { sortBy?: string; desc?: boolean }) => void;
  isSelectEnabled: boolean;
  setSelectEnabled: (value: boolean) => void;
};

export const PatientDuplicateTable: FC<Props> = React.memo(
  function PatientDuplicateTable(props) {
    const i18next = useScopedTranslation('merge_assistant.table');
    const forms = useGlobalForms();
    const {
      selectedPairs,
      setSelectedPairs,
      isSelectEnabled,
      setSelectEnabled,
    } = props;
    const tableRef = useRef<HTMLDivElement>(null);

    const lastActivityFieldName =
      nameof<PatientDuplicatePairDto>('lastActivity');
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(
      SortDirection.Desc,
    );
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    const changeSorting = useEventCallback((field: string) => {
      props.setSorting({
        sortBy: field,
        desc: sortDirection === SortDirection.Asc,
      });
      setSortDirection(
        sortDirection === SortDirection.Asc
          ? SortDirection.Desc
          : SortDirection.Asc,
      );
    });

    if (props.isNoDuplicatesFound) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>{i18next.t('empty')}</div>
        </div>
      );
    }
    return (
      <TableContainer
        ref={tableRef}
        className={styles.tableContainer}
        component={Paper}
      >
        <Table stickyHeader className={styles.table}>
          <TableHead>
            <TableRow>
              {isSelectEnabled && (
                <TableCell className={styles.header} key={'select-all'}>
                  <div className={styles.selection}>
                    <CheckBox
                      name={'select-all-checkbox'}
                      title={i18next.t('select_all')}
                      checked={props.data.length === selectedPairs.length}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setSelectedPairs(props.data.map((x) => x.id));
                        } else {
                          setSelectedPairs([]);
                        }
                      }}
                    />
                  </div>
                </TableCell>
              )}
              {columnKeys.map((columnKey) => (
                <TableCell
                  key={`${columnKey}`}
                  className={styles.header}
                  style={columnStyles[columnKey]}
                  onClick={() =>
                    columnKey === lastActivityFieldName &&
                    changeSorting(columnKey)
                  }
                >
                  <TableSortLabel
                    disabled={columnKey !== lastActivityFieldName}
                    active={
                      columnKey ===
                      nameof<PatientDuplicatePairDto>('lastActivity')
                    }
                    direction={
                      columnKey === lastActivityFieldName
                        ? sortDirection === SortDirection.Asc
                          ? 'asc'
                          : 'desc'
                        : undefined
                    }
                    style={{ userSelect: 'none' }}
                    className={styles.headerCell}
                  >
                    {i18next.t(columnKey as any)}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell
                className={styles.header}
                style={{ minWidth: 70 }}
                key={'enable-select'}
              >
                <div className={styles.optionHeader}>
                  <UniLink
                    icon={isSelectEnabled ? undefined : <ListIcon />}
                    title={
                      isSelectEnabled
                        ? i18next.t('select_done')
                        : i18next.t('select_enable')
                    }
                    onClick={() => {
                      setSelectedPairs([]);
                      setSelectEnabled(!isSelectEnabled);
                    }}
                    color={ButtonColor.Primary}
                  />
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((duplicate) => (
              <TableRow
                key={duplicate.id}
                hover
                onClick={() =>
                  forms.openMergePair(
                    duplicate.firstPatient.id,
                    duplicate.secondPatient.id,
                    duplicate.id,
                  )
                }
              >
                {isSelectEnabled && (
                  <TableCell onClick={(event: any) => event.stopPropagation()}>
                    <div>
                      <CheckBox
                        name={`select_checkbox_${duplicate.id}`}
                        title={''}
                        checked={selectedPairs.includes(duplicate.id)}
                        onChange={() => {
                          if (selectedPairs.includes(duplicate.id)) {
                            setSelectedPairs(
                              selectedPairs.filter((id) => id !== duplicate.id),
                            );
                          } else {
                            setSelectedPairs([...selectedPairs, duplicate.id]);
                          }
                        }}
                        testId="select-checkbox-for-row"
                      ></CheckBox>
                    </div>
                  </TableCell>
                )}
                <DuplicateCell
                  firstValue={
                    duplicate.firstPatient.number +
                    getNumberPostfix(
                      duplicate.firstPatient.numberSource,
                      i18next.t('external_patient_number'),
                    )
                  }
                  secondValue={
                    duplicate.secondPatient.number +
                    getNumberPostfix(
                      duplicate.secondPatient.numberSource,
                      i18next.t('external_patient_number'),
                    )
                  }
                />
                <DuplicateCell
                  firstValue={duplicate.firstPatient.firstName}
                  secondValue={duplicate.secondPatient.firstName}
                />
                <DuplicateCell
                  firstValue={duplicate.firstPatient.lastName}
                  secondValue={duplicate.secondPatient.lastName}
                />
                <DuplicateCell
                  firstValue={getFormattedDateRange(
                    duplicate.firstPatient.dateOfBirth,
                    duplicate.firstPatient.dateOfDeath,
                  )}
                  secondValue={getFormattedDateRange(
                    duplicate.secondPatient.dateOfBirth,
                    duplicate.secondPatient.dateOfDeath,
                  )}
                />

                <DuplicateCell
                  firstValue={duplicate.firstPatient.priorityContact}
                  secondValue={duplicate.secondPatient.priorityContact}
                />
                <TableCell>
                  <div className={styles.lastActivityContainer}>
                    {getFormattedDateTime(duplicate.lastActivity)}
                  </div>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
